import React, { useEffect } from "react";
import Icon from "../NGMaterialIcon/NGMaterialIcon";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Dialog as MUIDialog,
  Paper,
  PaperProps,
  Slide,
  SlideProps,
} from "@mui/material";
import { batch, useSignal, useSignalEffect } from "@preact/signals-react";
import { isBoolean, isNil, isString } from "lodash-es";
import { SimpleContainer } from "../../../resolvers-types";
import NGLayoutItem from "../../generators/NGLayoutItem";
import NGSimpleContainer from "../../generators/NGSimpleContainer";
import { INGDialogProps } from "../../library/NGFieldExtensions";
import { publishUserAction } from "../../library/analytics";
import { clearModalComponentState, setupHandlers, setupLocalState, updateItemContext } from "../../library/dataService";
import { log } from "../../library/logger";
import {
  getClassName,
  getClassNameFromString,
  getCustomLabel,
  getStyleObject,
  getTestId,
  getsxObject,
  isNullOrEmpty,
  partitionMUIClasses,
  toBoolean,
} from "../../library/utils";

import Draggable from "react-draggable";

const tag = "NGModalPopup";

const Transition = React.forwardRef(function Transition(props: SlideProps, ref: React.Ref<unknown>) {
  return <Slide direction={props.direction ?? "up"} ref={ref} {...props} />;
});

export default function NGModalPopup({ config, context }: INGDialogProps) {
  const currentContext = updateItemContext(context, config);

  const local = setupLocalState(
    config,
    {
      Open: useSignal(config.Open ?? false),
      ContentText: useSignal(config.ContentText ?? ""),
      ContentClasses: useSignal(config.ContentClasses ?? []),
      ContentStyle: useSignal(config.ContentStyle ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Style: useSignal(config.Style ?? {}),
      Title: useSignal(config.Title),
      ShowOkButton: useSignal(config.ShowOkButton),
      ShowCancelButton: useSignal(config.ShowCancelButton),
      ShowCloseButton: useSignal(config.ShowCloseButton ?? true),
      ButtonClicked: useSignal(config.ButtonClicked ?? "None"),
      TitleStyle: useSignal(config.TitleStyle ?? {}),
      FullCustom: useSignal(config.FullCustom ?? false),
      FullScreen: useSignal(config.FullScreen ?? false),
      Slide: useSignal(config.Slide ?? null),
      OnClose: config.OnClose ?? null,
    },
    currentContext
  );
  const handlers = setupHandlers(config, currentContext);
  const mod: any = useSignal(null);

  useEffect(() => {
    (async () => {
      const draggable = await import("@/lib/react-draggable/react-draggable-bundle.es.js");
      mod.value = draggable.default;
    })();
  }, []);

  const Draggable = mod.value;
  useSignalEffect(() => {
    if (local.Open.i > 0) {
      const [_, classes] = partitionMUIClasses(local.Classes.value);
      publishUserAction(config, context, local.Open.value ? "modal_open" : "modal_close", null, {
        EventCategory: "engagement",
        ElementText: local.Title.value || config.Id,
        ElementClass: classes.join(" "),
      });
    }

    // if the modal is being closed, auto clear its form so that it doesn't retain old state
    if (!local.Open.value) {
      clearModalComponentState(currentContext, config.Id);
    }
  });

  const closeModal = () => {
    local.Open.value = false; // TODO: do we always have to modify the whole object??
    if (!isNil(local.OnClose)) {
      log.info(tag, "onClose", local.ButtonClicked.value);
      local.OnClose();
    }
    if (handlers["onClose"]) {
      handlers["onClose"](config, config);
    }
  };

  const onClose = () => {
    batch(() => {
      // OpenModalPopup is called both when opening the modal and when closing it
      // so we set the button value here to "Closing" to indicate that the modal is closing
      // this is checked in actions.ts OpenModalPopup
      local.ButtonClicked.value = "Closing";
      closeModal();
    });
  };

  (config.ContentContainer as any).ContextId = (config.ContentContainer as any).ContextId ?? (config as any).ContextId;

  const titleId = "alert-dialog-title"; //getTestId(config, "DialogTitle");
  function PaperComponent(props: PaperProps) {
    return <Paper sx={getStyleObject(config.Style)} classes={config.Classes?.join(" ")} {...props} />;
  }

  function DraggablePaperComponent(props: PaperProps) {
    return (
      <>
        {Draggable && (
          <Draggable handle={`#${titleId}`} cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper sx={getStyleObject(config.Style)} classes={config.Classes?.join(" ")} {...props} />
          </Draggable>
        )}
      </>
    );
  }

  let fullScreen = false;
  if ((!isNil(local.FullScreen.value) && isString(local.FullScreen.value)) || isBoolean(local.FullScreen.value)) {
    fullScreen = toBoolean(local.FullScreen.value as any);
  }

  let sliderProps = {};
  if (local.Slide.value) {
    sliderProps = {
      slots: {
        transition: Transition,
      },
      slotProps: {
        transition: {
          direction: local.Slide.value,
        },
      },
      keepMounted: true,
      PaperProps: {
        sx: getsxObject(config.InnerDialogStyle, {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          m: 0,
          maxWidth: "none",
          width: "100%",
        }),
        classes: getClassNameFromString(config.InnerDialogClasses),
      },
    };
  }

  return (
    <>
      {local.FullCustom.value ? (
        <MUIDialog
          data-testid={getTestId(config)}
          open={local.Open.value}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullScreen={fullScreen}
          sx={getsxObject(local.Style.value)}
          PaperProps={{
            sx: getsxObject(config.InnerDialogStyle),
            classes: getClassNameFromString(config.InnerDialogClasses),
          }}
          disableScrollLock
          {...sliderProps}
        >
          <DialogContent sx={{ padding: 0 }}>
            {config.ContentContainer && (
              <NGSimpleContainer
                data-testid={getTestId(config, "DialogContent")}
                config={config.ContentContainer as SimpleContainer}
                context={currentContext}
              />
            )}
          </DialogContent>
        </MUIDialog>
      ) : (
        <MUIDialog
          data-testid={getTestId(config)}
          open={local.Open.value}
          onClose={onClose}
          PaperProps={{
            sx: getsxObject(config.InnerDialogStyle),
            classes: getClassNameFromString(config.InnerDialogClasses),
          }}
          PaperComponent={config.Draggable ? DraggablePaperComponent : PaperComponent}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullScreen={fullScreen}
          className={getClassName(local.Classes)}
          sx={getsxObject(local.Style.value)}
          disableScrollLock
          hideBackdrop={config.Draggable ? true : false}
          disableEnforceFocus={config.Draggable ? true : false}
          disableEscapeKeyDown={config.Draggable ? true : false}
          {...sliderProps}
        >
          {local.ShowCloseButton.value && (
            <IconButton
              data-testid={getTestId(config, "close")}
              onClick={onClose}
              style={{ position: "absolute", right: 16, top: 12 }}
            >
              <Icon name="close" />
            </IconButton>
          )}
          <DialogTitle id={titleId} data-testid={getTestId(config, "DialogTitle")} sx={local.TitleStyle.value}>
            <Box display="flex" justifyContent="center">
              {!isNullOrEmpty(local.Title.value) && getCustomLabel(local.Title.value)}
              {!isNil(config.HeaderItem) && <NGLayoutItem config={config.HeaderItem} context={currentContext} />}
            </Box>
          </DialogTitle>
          <DialogContent className={getClassName(local.ContentClasses)} sx={getsxObject(local.ContentStyle.value)}>
            {local.ContentText.value && (
              <DialogContentText id="alert-dialog-description" data-testid={getTestId(config, "DialogContextText")}>
                {local.ContentText.value}
              </DialogContentText>
            )}
            {config.ContentContainer && (
              <NGSimpleContainer
                data-testid={getTestId(config, "DialogContent")}
                config={config.ContentContainer as SimpleContainer}
                context={currentContext}
              />
            )}
          </DialogContent>
          {config?.FooterContainer && (
            <DialogActions>
              <NGSimpleContainer
                data-testid={getTestId(config, "DialogFooter")}
                config={config.FooterContainer as SimpleContainer}
                context={currentContext}
              />
            </DialogActions>
          )}
        </MUIDialog>
      )}
    </>
  );
}
